import { getMemoryTestPrompts, getScoreColor } from '../utils/scoring';
import TickSlider from './TickSlider';

export default function MemoryTest({ data, onChange }) {
  const prompts = getMemoryTestPrompts();

  // Ensure data has all required fields with defaults
  const safeData = {
    score: data?.score || 5,
    signatureMoments: data?.signatureMoments || [''],
    gaps: data?.gaps || [''],
    ...data,
  };

  const color = getScoreColor(safeData.score);

  const updateField = (field, value) => {
    onChange({ ...safeData, [field]: value });
  };

  const updateListItem = (field, index, value) => {
    const list = [...(safeData[field] || [])];
    list[index] = value;
    onChange({ ...safeData, [field]: list });
  };

  const addItem = (field) => {
    onChange({ ...safeData, [field]: [...(safeData[field] || []), ''] });
  };

  const removeItem = (field, index) => {
    const list = [...(safeData[field] || [])];
    list.splice(index, 1);
    onChange({ ...safeData, [field]: list });
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display text-lg text-white">The Memory Test</h3>
          <p className="text-xs text-neutral-500 mt-0.5">What would a guest remember 48 hours later?</p>
        </div>
        <div className={`w-14 h-14 rounded-xl ${color.bg} flex items-center justify-center`}>
          <span className={`text-xl font-black ${color.text}`}>{safeData.score.toFixed(1)}</span>
        </div>
      </div>

      {/* Score slider */}
      <div className="mb-5">
        <TickSlider value={safeData.score} onChange={(v) => updateField('score', v)} />
      </div>

      {/* Prompts */}
      <div className="bg-neutral-800/40 rounded-lg p-4 mb-5">
        <p className="text-xs text-brand-gold font-semibold mb-2 uppercase tracking-wider">Consider These Questions</p>
        <ul className="space-y-1.5">
          {prompts.map((p, i) => (
            <li key={i} className="text-xs text-neutral-400 flex items-start gap-2">
              <span className="text-brand-gold/60 mt-0.5">-</span>
              {p}
            </li>
          ))}
        </ul>
      </div>

      {/* Signature Moments */}
      <div className="mb-4">
        <label className="label-text text-emerald-400">Signature Moments (what guests WOULD remember)</label>
        {(safeData.signatureMoments || ['']).map((m, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="text"
              value={m}
              onChange={(e) => updateListItem('signatureMoments', i, e.target.value)}
              placeholder={`Moment ${i + 1}...`}
              className="input-field text-sm flex-1"
            />
            {safeData.signatureMoments.length > 1 && (
              <button onClick={() => removeItem('signatureMoments', i)} className="text-neutral-600 hover:text-red-400 text-xs">x</button>
            )}
          </div>
        ))}
        <button onClick={() => addItem('signatureMoments')} className="btn-ghost text-xs">+ Add moment</button>
      </div>

      {/* Gaps */}
      <div>
        <label className="label-text text-red-400">Memory Gaps (what's missing)</label>
        {(safeData.gaps || ['']).map((g, i) => (
          <div key={i} className="flex gap-2 mb-2">
            <input
              type="text"
              value={g}
              onChange={(e) => updateListItem('gaps', i, e.target.value)}
              placeholder={`Gap ${i + 1}...`}
              className="input-field text-sm flex-1"
            />
            {safeData.gaps.length > 1 && (
              <button onClick={() => removeItem('gaps', i)} className="text-neutral-600 hover:text-red-400 text-xs">x</button>
            )}
          </div>
        ))}
        <button onClick={() => addItem('gaps')} className="btn-ghost text-xs">+ Add gap</button>
      </div>
    </div>
  );
}
